import { signOut, updatePassword } from 'aws-amplify/auth'
import apiClient from '../client'
import { clearLocalDevAuth, isLocalDevLoginEnabled } from './localDev'
import type { LocalDevLoginResponse, UserProfileResponse } from './types'

export type CognitoErrorContext = 'signIn' | 'changePassword' | 'confirmSignIn'

export function translateCognitoError(err: unknown, context?: CognitoErrorContext): Error | null {
  const e = (err ?? {}) as { name?: string; message?: string }
  const name = typeof e.name === 'string' ? e.name : ''
  const message = typeof e.message === 'string' ? e.message : ''
  // Cognito が EMAIL MFA の認証コード検証で投げる素のメッセージ。
  // 名前は環境により NotAuthorizedException / CodeMismatchException 等まちまちなので
  // 本文一致で先に拾う (amplify-ui も同じ判定をしている)。
  if (/Invalid code or auth state/i.test(message)) {
    return new Error('認証コードが正しくありません。')
  }
  // MFA 必須のユーザープールで、対象アカウントに MFA 手段が未登録のとき Cognito が返す
  // 生メッセージ ("No MFA mechanism registerd in the account." ※AWS 側の綴り)。
  // パスワード検証後の MFA チャレンジ発行で失敗するため利用者自身では解決できない。
  // 綴り違い ("registerd"/"registered") に耐えるよう "no mfa mechanism" でマッチする。
  if (/no mfa mechanism/i.test(message)) {
    return new Error('多要素認証（MFA）が設定されていないため、ログインできません。管理者にお問い合わせください。')
  }
  if (name === 'NotAuthorizedException') {
    if (/user is disabled/i.test(message)) {
      return new Error('アカウントがロックされているか、無効化されています。管理者にお問い合わせください。')
    }
    if (context === 'confirmSignIn') {
      // confirmSignIn 段階で NotAuthorizedException が返るのは
      // 認証コード／サインインセッションの有効期限切れ・リトライ上限到達時。
      // この時点で username/password 自体は検証済みのため、有効期限切れとして案内する。
      return new Error('認証コードの有効期限が切れました。ログイン画面からやり直してください。')
    }
    if (context === 'signIn') {
      return new Error('ログインIDまたはパスワードが正しくありません。')
    }
    return new Error('現在のパスワードが正しくありません。')
  }
  if (name === 'UserNotFoundException') {
    return new Error('ログインIDまたはパスワードが正しくありません。')
  }
  if (name === 'SignInException') {
    // Amplify v6 の signInStore が期限切れ (デフォルト 3 分) になると confirmSignIn でこれを投げる。
    // EMAIL MFA はメール到着待ち + 入力で 3 分を超えやすく、よく踏む。
    return new Error('認証セッションの有効期限が切れました。ログイン画面からやり直してください。')
  }
  if (name === 'CodeMismatchException') {
    return new Error('認証コードが正しくありません。')
  }
  if (name === 'ExpiredCodeException') {
    return new Error('認証コードの有効期限が切れました。ログイン画面からやり直してください。')
  }
  if (name === 'InvalidPasswordException') {
    if (/symbol/i.test(message)) return new Error('パスワードに記号を含めてください。')
    if (/uppercase/i.test(message)) return new Error('パスワードに英大文字を含めてください。')
    if (/lowercase/i.test(message)) return new Error('パスワードに英小文字を含めてください。')
    if (/numeric/i.test(message)) return new Error('パスワードに数字を含めてください。')
    if (/long enough/i.test(message)) return new Error('パスワードは8文字以上で入力してください。')
    return new Error('パスワードがポリシーに適合しません。')
  }
  if (name === 'LimitExceededException') {
    return new Error('試行回数が上限に達しました。しばらくしてから再度お試しください。')
  }
  if (name === 'InvalidParameterException') {
    // Cognito の resetPassword (ForgotPassword) は、宛先メールアドレスがプール内の
    // 複数ユーザー (複数ログインID) で共有されていると送信先を一意に特定できず、
    // "Cannot reset password for the user as there is no registered/verified
    // email or phone_number" を返す (メール未登録/未認証時も同じメッセージ)。
    // email は新規作成時必須のため、実運用ではメールアドレス共有が主因。
    // セルフサービスでは解決できないため、共有が原因であることを伝えて管理者へ誘導する。
    if (/registered\/verified email|phone_number/i.test(message)) {
      return new Error('入力されたログインIDのメールアドレスは、他のログインIDでも使用されているため、この方法ではパスワードをリセットできません。管理者へお問い合わせください。')
    }
    return new Error('入力内容に誤りがあります。ご確認ください。')
  }
  if (name === 'PasswordResetRequiredException') {
    return new Error('パスワードのリセットが必要です。管理者にお問い合わせください。')
  }
  if (name === 'UserNotConfirmedException') {
    return new Error('アカウントの確認が完了していません。')
  }
  return null
}

export const authApi = {
  me() {
    return apiClient.get<UserProfileResponse>('/api/auth/me', { _silent: true })
  },

  loginSession() {
    return apiClient.post<UserProfileResponse>('/api/auth/login-session', undefined, { _silent: true })
  },

  devLogin(loginId: string, password: string) {
    return apiClient.post<LocalDevLoginResponse>('/api/auth/dev-login', { loginId, password }, { _silent: true })
  },

  reportLoginFailure(loginId: string) {
    // locked = この失敗でアカウントが新たにロックされたか（5 回目の失敗で true）。
    // lastChance = あと 1 回でロックされる状態か（4 回目の失敗・残り 1 回で true）。
    return apiClient.post<{ locked: boolean; lastChance: boolean }>('/api/auth/report-login-failure', { loginId }, { _silent: true })
  },

  checkAccountLock(loginId: string) {
    return apiClient.post<void>('/api/auth/check-account-lock', { loginId }, { _silent: true })
  },

  // 自助パスワードリセット①: ログインIDで確認コードのメール送信を要求する。
  // バックエンドはアカウント存在を漏らさないため常に成功応答（Cognito ForgotPassword は使わない）。
  forgotPassword(loginId: string) {
    return apiClient.post<{ message: string }>('/api/auth/forgot-password', { loginId }, { _silent: true })
  },

  // 自助パスワードリセット②: 確認コード＋新パスワードで改密する。
  resetPassword(loginId: string, code: string, newPassword: string) {
    return apiClient.post<{ message: string }>('/api/auth/reset-password', { loginId, code, newPassword }, { _silent: true })
  },

  async changePassword(oldPassword: string, newPassword: string) {
    try {
      await updatePassword({ oldPassword, newPassword })
    } catch (err: unknown) {
      const translated = translateCognitoError(err, 'changePassword')
      if (translated) throw translated
      throw err
    }
  },

  async logout() {
    try {
      await apiClient.post('/api/auth/logout', undefined, { _silent: true })
    } catch { /* ignore — best effort */ }
    clearLocalDevAuth()
    if (!isLocalDevLoginEnabled()) {
      await signOut()
    }
  },
}
